import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';

/**
 * Phase 4 — Export-report button.
 *
 * Hits Phase 3's `GET /api/runs/{id}/report?format={pdf|docx}` with the
 * caller's bearer token, streams the binary into a blob URL, then
 * triggers a synthetic anchor click so the browser saves it. The
 * Phase 3 endpoint persists `runtime/output/{id}/report.{ext}` as a
 * side effect — re-clicks regenerate idempotently.
 */
export const ExportReportButton = ({ runId, disabled = false }) => {
  const { token } = useAuth();
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const downloadReport = async (format) => {
    if (busy) return;
    setBusy(format);
    setError('');
    try {
      const url =
        `${process.env.REACT_APP_BACKEND_URL}/api/runs/${runId}/report?format=${format}`;
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`HTTP ${r.status} — ${detail.slice(0, 200)}`);
      }
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `uavsimnet-run-${runId}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Keep the URL alive briefly so the browser can pick it up before GC.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 8000);
    } catch (e) {
      setError(e.message || 'Report generation failed.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div
      data-testid="export-report-control"
      style={{ display: 'inline-flex', gap: '0.4rem', alignItems: 'center' }}
    >
      <button
        type="button"
        data-testid="export-report-pdf-btn"
        onClick={() => downloadReport('pdf')}
        disabled={disabled || !!busy}
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.72rem',
          padding: '6px 12px',
          borderRadius: 6,
          background: busy === 'pdf' ? '#3a4654' : '#0f1419',
          color: '#eef3f8',
          border: '1px solid #2c3a48',
          cursor: busy || disabled ? 'not-allowed' : 'pointer',
          letterSpacing: '0.04em',
        }}
      >
        {busy === 'pdf' ? '· Generating…' : 'Export PDF'}
      </button>
      <button
        type="button"
        data-testid="export-report-docx-btn"
        onClick={() => downloadReport('docx')}
        disabled={disabled || !!busy}
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.72rem',
          padding: '6px 12px',
          borderRadius: 6,
          background: busy === 'docx' ? '#3a4654' : 'transparent',
          color: '#0f1419',
          border: '1px solid #0f1419',
          cursor: busy || disabled ? 'not-allowed' : 'pointer',
          letterSpacing: '0.04em',
        }}
      >
        {busy === 'docx' ? '· Generating…' : 'Export DOCX'}
      </button>
      {error && (
        <span
          data-testid="export-report-error"
          style={{ color: '#b15a00', fontSize: '0.7rem', marginLeft: '0.5rem' }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default ExportReportButton;
